import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Search, RefreshCw, History, User, Activity,
    Calendar, CheckCircle2, FileText,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, ArrowUpDown, Loader2
} from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError } from "../../utils/toast";
import { ItemsPerPageDropdown } from "../../components/ItemsPerPageDropdown ";
import { useClientLevel } from "../../hooks/useClientLevel";

interface AuditLog {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    user: string;
    action: string;
    organization_id: number;
    client_id: number;
    details: string;
    status: string;
    record_id: string;
    previous_values: string;
    current_values: string;
}

export const AuditLogs = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);

    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // Sync with URL params
    const [currentPage, setCurrentPage] = useState<number>(() => {
        const p = searchParams.get('page');
        return p ? parseInt(p, 10) : 1;
    });

    const [itemsPerPage, setItemsPerPage] = useState<number>(() => {
        const l = searchParams.get('limit');
        return l ? parseInt(l, 10) : 30;
    });

    const [sortBy, setSortBy] = useState<string>(searchParams.get('sort_by') || 'created');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>((searchParams.get('sort_order') as 'asc' | 'desc') || 'desc');

    const level = useClientLevel();

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // Sync state from URL params
    useEffect(() => {
        const p = searchParams.get('page');
        const l = searchParams.get('limit');
        const sb = searchParams.get('sort_by');
        const so = searchParams.get('sort_order');

        if (p) {
            const pageNum = parseInt(p, 10);
            if (!isNaN(pageNum) && pageNum !== currentPage) setCurrentPage(pageNum);
        }
        if (l) {
            const limitNum = parseInt(l, 10);
            if (!isNaN(limitNum) && limitNum !== itemsPerPage) setItemsPerPage(limitNum);
        }
        if (sb && sb !== sortBy) setSortBy(sb);
        if (so && (so === 'asc' || so === 'desc') && so !== sortOrder) setSortOrder(so as 'asc' | 'desc');
    }, [searchParams, currentPage, itemsPerPage, sortBy, sortOrder]);

    // Debounce search
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
                setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('page', '1');
                    return next;
                }, { replace: true });
            }
        }, 500);

        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch, setSearchParams]);

    const fetchLogs = useCallback(async (skip = 0, limit = itemsPerPage) => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (level?.id) {
                queryObj["client_id"] = level.id;
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { user: { $ilike: `%${debouncedSearch}%` } },
                    { details: { $ilike: `%${debouncedSearch}%` } },
                    { action: { $ilike: `%${debouncedSearch}%` } }
                ];
            }

            // Build API payload
            const payload: any = {
                skip,
                limit,
                sort_by: sortBy,
                sort_order: sortOrder,
                query: JSON.stringify(queryObj)
            };

            const response = await baseapi.get("/audit_logs", { params: payload });

            if (response.data.status) {
                setLogs(response.data.data);
                setTotalPages(Math.ceil((response.data.total || response.data.data.length) / limit));
                setTotalItems(response.data.total || response.data.data.length);
            } else {
                setLogs([]);
                setTotalPages(0);
                setTotalItems(0);
            }
        } catch (error: any) {
            showError(error.message);
        } finally {
            setLoading(false);
        }
    }, [itemsPerPage, debouncedSearch, level, sortBy, sortOrder]);

    useEffect(() => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    }, [currentPage, itemsPerPage, fetchLogs]);

    const handleRefresh = () => {
        fetchLogs((currentPage - 1) * itemsPerPage, itemsPerPage);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
            }, { replace: true });
        }
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(1);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('limit', String(value));
            next.set('page', '1');
            return next;
        }, { replace: true });
    };

    const handleSort = (field: string) => {
        let nextOrder: 'asc' | 'desc' = 'asc';
        if (sortBy === field) {
            nextOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        }
        setSortBy(field);
        setSortOrder(nextOrder);
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('sort_by', field);
            next.set('sort_order', nextOrder);
            return next;
        }, { replace: true });
    };

    const SortIcon = ({ field }: { field: string }) => {
        if (sortBy !== field) return <ArrowUpDown className="w-2.5 h-2.5 ml-1 text-slate-300 transition-opacity" />;
        return sortOrder === 'asc'
            ? <ArrowUp className="w-2.5 h-2.5 ml-1 text-[#422568]" />
            : <ArrowDown className="w-2.5 h-2.5 ml-1 text-[#422568]" />;
    };

    const parseDescription = (log: AuditLog) => {
        try {
            const parsed = JSON.parse(log.details);

            if (log.action === "camera_asset_mac_conflict_merge") {
                return `
                    <div class="flex flex-col gap-0.5">
                        <span class="text-[#422568] font-bold">${parsed.message || 'Camera MAC Conflict Merge'}</span>
                        <div class="flex items-center gap-3 opacity-80 text-[8px] uppercase tracking-wider">
                            <span>MAC: <span class="text-blue-600 font-bold">${parsed.mac_address || 'N/A'}</span></span>
                            <span>KEPT ID: <span class="text-emerald-600 font-bold">${parsed.kept_asset_id || '-'}</span></span>
                            <span>REMOVED ID: <span class="text-rose-600 font-bold">${parsed.removed_asset_id || '-'}</span></span>
                        </div>
                    </div>
                `;
            }

            return parsed.description || parsed.message || "No description provided";
        } catch {
            return log.details || "-";
        }
    };

    const renderActionBadge = (action: string) => {
        const normalized = action.toLowerCase();
        let colorClass = "bg-slate-100 text-slate-500 border-slate-200";

        if (normalized.includes("update") || normalized.includes("edit")) {
            colorClass = "bg-blue-50 text-blue-600 border-blue-100";
        } else if (normalized.includes("create") || normalized.includes("add")) {
            colorClass = "bg-emerald-50 text-emerald-600 border-emerald-100";
        } else if (normalized.includes("delete") || normalized.includes("remove")) {
            colorClass = "bg-rose-50 text-rose-600 border-rose-100";
        } else if (normalized.includes("merge")) {
            colorClass = "bg-[#422568] text-[#422568] border-[#422568]";
        }

        return (
            <span className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-widest border shadow-sm ${colorClass}`}>
                {action.replace(/_/g, " ")}
            </span>
        );
    };

    return (
        <div className="bg-slate-50 p-3 min-h-screen pb-20">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-[#422568] p-1.5 rounded">
                            <History className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <h1 className="font-black text-lg text-slate-800 leading-none">Audit Logs</h1>
                            <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em] mt-1">Activity Tracking System</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className="rounded-lg bg-white border border-slate-200 text-slate-400 p-2 hover:bg-slate-50 hover:text-slate-800 transition-all shadow-sm active:scale-95"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#422568]' : ''}`} />
                        </button>
                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search logs..."
                                className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-slate-800 focus:border-[#422568] focus:ring-4 focus:ring-[#422568]/5 outline-none transition-all font-bold placeholder:text-slate-400 shadow-sm"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Main Table Container */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xl shadow-slate-200/50 mt-4">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                <tr className="border-b border-slate-100">
                                    <th
                                        className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('user')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <User size={12} className="text-slate-300 group-hover:text-blue-500 transition-colors" /> User <SortIcon field="user" />
                                        </div>
                                    </th>

                                    <th
                                        className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('action')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Activity size={12} className="text-slate-300 group-hover:text-purple-500 transition-colors" /> Action <SortIcon field="action" />
                                        </div>
                                    </th>
                                    <th className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                        <div className="flex items-center gap-2">
                                            <FileText size={12} className="text-slate-300" /> Description
                                        </div>
                                    </th>
                                    <th
                                        className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('status')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <CheckCircle2 size={12} className="text-slate-300 group-hover:text-emerald-500 transition-colors" /> Status <SortIcon field="status" />
                                        </div>
                                    </th>
                                    <th
                                        className="px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest cursor-pointer group hover:bg-slate-100 transition-colors"
                                        onClick={() => handleSort('created')}
                                    >
                                        <div className="flex items-center gap-2">
                                            <Calendar size={12} className="text-slate-300 group-hover:text-amber-500 transition-colors" /> Timestamp <SortIcon field="created" />
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="py-16 text-center text-slate-400 font-black uppercase tracking-widest animate-pulse">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-[#422568]" /> Fetching Logs
                                            </div>
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="py-16 text-center text-slate-300 font-black uppercase tracking-widest italic bg-slate-50/20">
                                            <div className="flex flex-col items-center gap-2">
                                                <History className="w-8 h-8 text-slate-100" /> No logs found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-slate-50 transition-colors group">
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500 group-hover:bg-[#422568] group-hover:text-white transition-colors shadow-sm">
                                                        {log.user.charAt(0)}
                                                    </div>
                                                    <span className="text-slate-800 text-[10px] font-bold leading-tight">
                                                        {log.user}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {renderActionBadge(log.action)}
                                            </td>
                                            <td className="px-3 py-2">
                                                <div
                                                    className="text-slate-600 text-[9px] leading-relaxed max-w-2xl font-bold font-mono"
                                                    dangerouslySetInnerHTML={{ __html: parseDescription(log) }}
                                                />
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[9px] font-black uppercase border shadow-sm ${log.status.toLowerCase() === 'success'
                                                    ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                                    : 'bg-rose-50 text-rose-600 border-rose-100'
                                                    }`}>
                                                    <div className={`w-1.5 h-1.5 rounded-full ${log.status.toLowerCase() === 'success' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.4)]'
                                                        }`} />
                                                    {log.status}
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span className="text-slate-400 text-[9px] font-mono font-bold flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                                                    {log.created
                                                        ? new Date(log.created.slice(0, 23) + "Z").toLocaleString()
                                                        : "-"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="bg-slate-50/50 border-t border-slate-100 px-6 py-2.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <ItemsPerPageDropdown
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                            />
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                                <span className="text-slate-800">
                                    {totalItems === 0 ? 0 : startIndex + 1}
                                </span>
                                {" - "}
                                <span className="text-slate-800">
                                    {Math.min(endIndex, totalItems)}
                                </span>
                                {" OF "}
                                {totalItems}
                            </span>
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"
                            >
                                <ChevronsLeft className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"
                            >
                                <ChevronLeft className="w-3.5 h-3.5" />
                            </button>

                            <div className="px-2.5 py-1 bg-[#422568] rounded-md text-white text-[10px] font-black mx-1.5">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"
                            >
                                <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                            <button
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"
                            >
                                <ChevronsRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
